import React, { useEffect, useState } from "react";
import CarrierHeader from "../../components/CarrierHeader";
import SubscriptionStatus from "../../components/SubscriptionStatus";
import api from "../../utils/axiosInstance";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { Link } from "react-router-dom";

const CarrierDashboard = () => {
  const [carrier, setCarrier] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [interestedCount, setInterestedCount] = useState(0);
  const [accountType, setAccountType] = useState("owner");

  // Edit Profile states
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [editSuccess, setEditSuccess] = useState(false);

  // Sub User Modal states
  const [subUserOpen, setSubUserOpen] = useState(false);
  const [subUserForm, setSubUserForm] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [subUserLoading, setSubUserLoading] = useState(false);
  const [subUserError, setSubUserError] = useState("");
  const [subUserSuccess, setSubUserSuccess] = useState("");

  // Manager states
  const [manager, setManager] = useState(null);
  const [managerExists, setManagerExists] = useState(false);
  const [editingManager, setEditingManager] = useState(false);

  // Add subscription hook - get subscription data and validation function
  const { fetchSubscription, subscription, isSubscriptionValid } =
    useSubscription();

  // Check if subscription is valid (active and not expired)
  const hasValidSubscription = isSubscriptionValid();

  useEffect(() => {
    const initializeDashboard = async () => {
      const stored = localStorage.getItem("carrierUser");
      if (!stored) return;

      const carrierStored = JSON.parse(stored);
      setAccountType(carrierStored.accountType || "owner");

      if (!carrierStored._id) return;

      try {
        // Fetch subscription status
        await fetchSubscription(carrierStored._id);

        // Fetch carrier data
        const res = await api.get(`/carriers/${carrierStored._id}`);
        const carrierData = res.data.carrier;
        const assigned =
          carrierData?.assignedDrivers
            ?.map((ad) => ad.driver)
            .filter(Boolean) || [];

        setCarrier(carrierData);
        setDrivers(assigned);

        // Fetch interested count
        try {
          const interestedRes = await api.get(
            `/interests/count/carrier/${carrierStored._id}`
          );
          setInterestedCount(interestedRes.data.count || 0);
        } catch (err) {
          setInterestedCount(0);
        }

        // Fetch manager details
        try {
          const managerRes = await api.get(
            `/carriers/${carrierStored._id}/manager`
          );
          if (managerRes.data.success) {
            setManager(managerRes.data.manager);
            setManagerExists(true);
          }
        } catch (err) {
          setManagerExists(false);
          setManager(null);
        }
      } catch (err) {
        console.error("Error initializing dashboard:", err);
      }
    };

    initializeDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  const getDashboardDate = () => {
    const today = new Date();
    return today.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Edit Profile handlers
  const openEdit = () => {
    if (carrier) {
      setEditForm({
        ...carrier,
        trailer: Array.isArray(carrier.trailer)
          ? carrier.trailer.join(", ")
          : carrier.trailer || "",
      });
      setEditOpen(true);
      setEditError("");
      setEditSuccess(false);
    }
  };

  const handleField = (field, value) => {
    setEditForm((f) => ({
      ...f,
      [field]: value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setEditError("");
    setEditSuccess(false);
    try {
      const { identificationNumber, _id, trailer, ...payload } = editForm;
      payload.trailer = trailer
        ? trailer
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [];

      payload.carrierId = carrier._id;

      await api.post("/carrierEdits", payload);

      setEditSuccess(true);
      alert("Your profile will be updated once admin approves");
      setEditOpen(false);
    } catch (err) {
      setEditError(err.response?.data?.message || "Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  // Sub User Modal handlers
  const openSubUserModal = () => {
    if (managerExists) {
      // If manager exists, show details in view mode
      setSubUserForm({
        name: manager.name || "",
        email: manager.email || "",
        password: manager.password || "",
      });
      setEditingManager(false); // Start in view mode
    } else {
      // New manager
      setSubUserForm({ name: "", email: "", password: "" });
      setEditingManager(false);
    }
    setSubUserOpen(true);
    setSubUserError("");
    setSubUserSuccess("");
  };

  const handleSubUserField = (field, value) => {
    setSubUserForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateSubUser = async (e) => {
    e.preventDefault();
    setSubUserLoading(true);
    setSubUserError("");
    setSubUserSuccess("");

    try {
      const payload = {
        carrierId: carrier._id,
        name: subUserForm.name,
        email: subUserForm.email,
        password: subUserForm.password,
      };

      await api.post("/carriers/create-manager", payload);

      setSubUserSuccess("Manager created successfully!");
      alert("Manager created successfully!");
      setSubUserOpen(false);
      setSubUserForm({ name: "", email: "", password: "" });

      // Refresh manager details
      const res = await api.get(`/carriers/${carrier._id}/manager`);
      if (res.data.success) {
        setManager(res.data.manager);
        setManagerExists(true);
      }
    } catch (err) {
      setSubUserError(
        err.response?.data?.message || "Failed to create sub user. Try again."
      );
    } finally {
      setSubUserLoading(false);
    }
  };

  const handleUpdateManager = async (e) => {
    e.preventDefault();
    setSubUserLoading(true);
    setSubUserError("");
    setSubUserSuccess("");

    try {
      const payload = {
        name: subUserForm.name,
        email: subUserForm.email,
        password: subUserForm.password,
      };

      await api.put(`/carriers/${carrier._id}/manager`, payload);

      alert("Manager details updated successfully!");
      setSubUserOpen(false);
      setEditingManager(false);

      // Refresh manager details
      const res = await api.get(`/carriers/${carrier._id}/manager`);
      if (res.data.success) {
        setManager(res.data.manager);
      }
    } catch (err) {
      setSubUserError(
        err.response?.data?.message || "Failed to update manager. Try again."
      );
    } finally {
      setSubUserLoading(false);
    }
  };

  const handleDeleteManager = async () => {
    if (!window.confirm("Are you sure you want to delete this manager?")) {
      return;
    }

    try {
      await api.delete(`/carriers/${carrier._id}/manager`);
      alert("Manager deleted successfully!");
      setManager(null);
      setManagerExists(false);
      setSubUserOpen(false);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to delete manager");
    }
  };

  //  Just enable edit mode, don't submit
  const enableManagerEdit = () => {
    setEditingManager(true);
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <CarrierHeader />
      <div className="sm:ml-64 px-4 py-8 max-w-full">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-primary">
            Carrier Dashboard
            <span className="ml-3 text-sm text-gray-500 font-normal">
              {getDashboardDate()}
            </span>
            <div className="mt-2">
              {accountType === "owner" ? (
                <div className="bg-blue-100 text-blue-800 text-lg font-medium mr-2 px-2.5 py-0.5 rounded">
                  Logged in as Carrier Owner <br />
                  <span className="text-black text-sm"> {carrier?.email}</span>
                </div>
              ) : (
                <div className="bg-blue-100 text-blue-800 text-lg font-medium mr-2 px-2.5 py-0.5 rounded">
                  Logged in as Carrier Manager <br />
                  <span className="text-black text-sm">
                    {" "}
                    {carrier?.managerEmail}
                  </span>
                </div>
              )}
            </div>
          </h2>

          {/* Action Buttons - Only show for owners with valid subscription */}
          {accountType === "owner" && hasValidSubscription && (
            <div className="flex gap-3">
              <button
                onClick={openSubUserModal}
                className="bg-primary text-white rounded-full px-4 py-2 hover:bg-black transition"
                aria-label="Add Sub User"
                title={
                  managerExists ? "View/Edit Manager" : "Add Carrier Manager"
                }
              >
                <i
                  className={`fas fa-${
                    managerExists ? "eye" : "user-plus"
                  } mr-2`}
                />{" "}
                {managerExists ? "View Manager" : "Add Manager"}
              </button>
              <button
                onClick={openEdit}
                className="bg-primary text-white rounded-full px-4 py-2 hover:bg-black transition"
                aria-label="Edit Profile"
                title="Edit Profile"
              >
                <i className="fas fa-edit mr-2" /> Edit Profile
              </button>
            </div>
          )}
        </div>
        <div className="col-span-full mb-6">
          <SubscriptionStatus />
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 mb-10 w-full relative">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-6 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-light-gray shadow-md bg-gray-400 flex items-center justify-center">
                <img
                  src={
                    carrier?.companyLogo
                      ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${carrier.companyLogo}`
                      : "/assets/dummy-icon.jpg"
                  }
                  alt="Logo"
                  className="w-full h-full object-cover bg-gray-300"
                />
              </div>
              <h3 className="text-xl font-bold text-primary">
                {carrier?.companyName}
              </h3>
            </div>

            {/* Only show Available Drivers and Favorites if subscription is valid */}
            {hasValidSubscription && (
              <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  {
                    to: "/carrier/assigned-drivers",
                    title: "Available Drivers",
                    count: drivers.length,
                    icon: "fas fa-male",
                  },
                  {
                    to: "/carrier/favorites",
                    title: "Favorites",
                    count: interestedCount,
                    icon: "fas fa-users",
                  },
                ].map((item, index) => (
                  <Link to={item.to} key={index}>
                    <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary hover:-translate-y-1 hover:shadow-xl transition duration-300 h-full">
                      <div className="flex justify-between items-center h-full">
                        <div className="flex flex-col justify-center">
                          <p className="text-gray-500 text-sm font-medium">
                            {item.title}
                          </p>
                          <h3 className="text-2xl font-bold mt-1 text-gray-900">
                            {item.count}
                          </h3>
                        </div>
                        <div className="ml-4 p-3 rounded-lg bg-primary/10 text-primary flex items-center justify-center w-12 h-12">
                          <i className={`${item.icon} text-2xl`} />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </section>
            )}
          </div>

          <div className="mb-10 bg-blue-100 p-4 rounded grid grid-cols-1 md:grid-cols-2 gap-4 p-7">
            <div>
              <h3 className="text-lg font-bold mb-2">Live Demo / Onboarding</h3>
              <p className="mb-5">
                Schedule an onboarding session with our experts to get started.
              </p>
              <Link
                to={"/carrier/live-demo-request"}
                className="bg-primary px-3 py-2 text-white rounded hover:bg-primary-dark transition"
              >
                Schedule Now
              </Link>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Demo Videos</h3>
              <p className="mb-5">Explore our demo videos to learn more. </p>
              <Link
                to={"/carrier/watch-demo"}
                className="bg-primary px-3 py-2 text-white rounded hover:bg-primary-dark transition"
              >
                Watch Now
              </Link>
            </div>
          </div>

          {/* Dashboard Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-10 text-base">
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">
                Identification Number
              </p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.identificationNumber || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">
                Company Name
              </p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.companyName || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">
                Contact Person
              </p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.contactName || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">Job Role</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.jobTitle || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">Email</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.email || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">Phone</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.phone || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">Address</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.address || ""}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">City</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.city || ""}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">State</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.state || ""}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">ZIP Code</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.zip || ""}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">Website</p>
              <h3 className="text-base font-bold text-gray-900">
                {carrier?.website || "N/A"}
              </h3>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">
                Equipment Hauled
              </p>
              <h3 className="text-base font-bold text-gray-900">
                {Array.isArray(carrier?.trailer)
                  ? carrier.trailer.join(", ")
                  : carrier?.trailer || "N/A"}
              </h3>
            </div>
            <div className="col-span-full bg-white p-4 rounded-xl shadow-sm border-l-4 border-primary">
              <p className="text-gray-500 text-sm font-medium mb-1">
                Company Description
              </p>
              <h3 className="text-base font-bold text-gray-900 whitespace-pre-line">
                {carrier?.companyDesc || "N/A"}
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal - Only accessible by owners */}
      {editOpen && accountType === "owner" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-2xl bg-white rounded shadow-lg relative overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-xl font-bold">Edit Profile</h3>
              <button
                onClick={() => setEditOpen(false)}
                className="text-gray-400 hover:text-blue-600 text-2xl"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <form
              className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4"
              onSubmit={handleSaveEdit}
            >
              <div>
                <label className="block font-semibold mb-1">Company Name</label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.companyName || ""}
                  onChange={(e) => handleField("companyName", e.target.value)}
                  required
                />
                <label className="block font-semibold mb-1 mt-4">
                  Contact Person
                </label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.contactName || ""}
                  onChange={(e) => handleField("contactName", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">
                  Job Role
                </label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.jobTitle || ""}
                  onChange={(e) => handleField("jobTitle", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">Email</label>
                <input
                  type="email"
                  className="border p-2 rounded w-full"
                  value={editForm?.email || ""}
                  onChange={(e) => handleField("email", e.target.value)}
                  required
                />
                <label className="block font-semibold mb-1 mt-4">Phone</label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.phone || ""}
                  onChange={(e) => handleField("phone", e.target.value)}
                />
              </div>
              <div>
                <label className="block font-semibold mb-1">Address</label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.address || ""}
                  onChange={(e) => handleField("address", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">City</label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.city || ""}
                  onChange={(e) => handleField("city", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">State</label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.state || ""}
                  onChange={(e) => handleField("state", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">
                  ZIP Code
                </label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.zip || ""}
                  onChange={(e) => handleField("zip", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">Website</label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.website || ""}
                  onChange={(e) => handleField("website", e.target.value)}
                />
                <label className="block font-semibold mb-1 mt-4">
                  Equipment Hauled
                </label>
                <input
                  className="border p-2 rounded w-full"
                  value={editForm?.trailer || ""}
                  onChange={(e) => handleField("trailer", e.target.value)}
                  placeholder="Comma separated (e.g. Refrigerated, Flatbed)"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block font-semibold mb-1 mt-4">
                  Company Description
                </label>
                <textarea
                  className="border p-2 rounded w-full min-h-[56px]"
                  value={editForm?.companyDesc || ""}
                  onChange={(e) => handleField("companyDesc", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block font-semibold mb-1 mt-4">
                  Identification Number
                </label>
                <input
                  className="border p-2 rounded w-full bg-gray-200 cursor-not-allowed"
                  value={editForm?.identificationNumber || ""}
                  disabled
                />
              </div>
              <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  className="bg-gray-400 text-white px-4 py-2 rounded"
                  onClick={() => setEditOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-primary text-white px-4 py-2 rounded font-semibold"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Edits"}
                </button>
              </div>
              {editError && (
                <div className="md:col-span-2 text-blue-600 mt-2">
                  {editError}
                </div>
              )}
              {editSuccess && (
                <div className="md:col-span-2 text-blue-600 mt-2">
                  Saved! You can close now.
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* Manager Modal - View/Edit/Delete/Create */}
      {subUserOpen && accountType === "owner" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md bg-white rounded shadow-lg relative">
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h3 className="text-xl font-bold">
                {managerExists
                  ? editingManager
                    ? "Edit Manager"
                    : "Manager Details"
                  : "Add Carrier Manager"}
              </h3>
              <button
                onClick={() => {
                  setSubUserOpen(false);
                  setEditingManager(false);
                }}
                className="text-gray-400 hover:text-red-600 text-2xl"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            {/* Separate view and edit/create modes */}
            {managerExists && !editingManager ? (
              // VIEW MODE - Not a form
              <div className="p-6 space-y-4">
                <div>
                  <label className="block font-semibold mb-1">Name</label>
                  <input
                    type="text"
                    className="border p-2 rounded w-full bg-gray-100 cursor-not-allowed"
                    value={subUserForm.name}
                    disabled
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Email</label>
                  <input
                    type="email"
                    className="border p-2 rounded w-full bg-gray-100 cursor-not-allowed"
                    value={subUserForm.email}
                    disabled
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Password</label>
                  <input
                    type="text"
                    className="border p-2 rounded w-full bg-gray-100 cursor-not-allowed"
                    value={subUserForm.password}
                    disabled
                  />
                </div>

                <div className="flex justify-between gap-3 mt-6">
                  <button
                    type="button"
                    className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                    onClick={handleDeleteManager}
                  >
                    <i className="fas fa-trash mr-2" />
                    Delete Manager
                  </button>
                  <button
                    type="button"
                    className="bg-primary text-white px-4 py-2 rounded hover:bg-black transition"
                    onClick={() => setEditingManager(true)}
                  >
                    <i className="fas fa-edit mr-2" />
                    Edit Details
                  </button>
                </div>
              </div>
            ) : (
              // EDIT/CREATE MODE - Form with submission
              <form
                className="p-6 space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (managerExists) {
                    handleUpdateManager(e);
                  } else {
                    handleCreateSubUser(e);
                  }
                }}
              >
                <div>
                  <label className="block font-semibold mb-1">Name</label>
                  <input
                    type="text"
                    className="border p-2 rounded w-full"
                    value={subUserForm.name}
                    onChange={(e) => handleSubUserField("name", e.target.value)}
                    placeholder="Enter manager name"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Email</label>
                  <input
                    type="email"
                    className="border p-2 rounded w-full"
                    value={subUserForm.email}
                    onChange={(e) =>
                      handleSubUserField("email", e.target.value)
                    }
                    placeholder="Enter manager email"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Password</label>
                  <input
                    type="text"
                    className="border p-2 rounded w-full"
                    value={subUserForm.password}
                    onChange={(e) =>
                      handleSubUserField("password", e.target.value)
                    }
                    placeholder="Enter password"
                    required
                  />
                </div>

                {subUserError && (
                  <div className="text-red-600 text-sm">{subUserError}</div>
                )}
                {subUserSuccess && (
                  <div className="text-blue-600 text-sm">{subUserSuccess}</div>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    className="bg-gray-400 text-white px-4 py-2 rounded"
                    onClick={() => {
                      if (managerExists) {
                        // Cancel edit mode, go back to view
                        setEditingManager(false);
                        // Restore original values
                        setSubUserForm({
                          name: manager.name || "",
                          email: manager.email || "",
                          password: manager.password || "",
                        });
                      } else {
                        // Close modal for create
                        setSubUserOpen(false);
                      }
                    }}
                    disabled={subUserLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary text-white px-4 py-2 rounded font-semibold hover:bg-black transition"
                    disabled={subUserLoading}
                  >
                    {subUserLoading
                      ? managerExists
                        ? "Updating..."
                        : "Creating..."
                      : managerExists
                      ? "Save Changes"
                      : "Create Manager"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierDashboard;
